import { useMemo } from 'react';
import { cratChain } from 'shared/config/wagmi';
import { citCrowdsaleContract, wusdtContract } from 'shared/config/wallet-service/contracts';
import { useTokenBalance } from 'shared/hooks';
import { useAccount, useReadContract, useReadContracts } from 'wagmi';

import { SaleToken, wusdtToken } from '../../model/sale-token';

type ReferralInfo = {
  purchaseMade: boolean;
  referrer: string;
  earned: bigint;
};

type Loading = {
  isBalanceLoading: boolean;
  isPublicSaleInfoLoading: boolean;
  isReferralInfoLoading: boolean;
};

export type PaymentToken = SaleToken & {
  balance: bigint | undefined;
};

interface CitSaleData {
  paymentToken: PaymentToken;
  referralInfo: ReferralInfo | undefined;
  citPrice: bigint | undefined;
  minPaymentAmount: bigint | undefined;
  totalSold: bigint | undefined;
  loading: Loading;
  isPaused: boolean;
}

export const useCitSaleData = (): CitSaleData => {
  const { address } = useAccount();

  const { data: tokenBalance, isLoading: isBalanceLoading } = useTokenBalance({
    token: wusdtContract.address,
    address,
    isEnabled: Boolean(address),
  });

  const {
    data: [citPriceResponse, minPaymentResponse, totalSoldResponse, pausedResponse] = [],
    isPending: isPublicSaleInfoLoading,
  } = useReadContracts({
    contracts: [
      {
        abi: citCrowdsaleContract.abi,
        address: citCrowdsaleContract.address,
        functionName: 'PRICE',
        chainId: cratChain.id,
      },
      {
        abi: citCrowdsaleContract.abi,
        address: citCrowdsaleContract.address,
        functionName: 'MIN_PAYMENT_AMOUNT',
        chainId: cratChain.id,
      },
      {
        abi: citCrowdsaleContract.abi,
        address: citCrowdsaleContract.address,
        functionName: 'totalSold',
        chainId: cratChain.id,
      },
      {
        abi: citCrowdsaleContract.abi,
        address: citCrowdsaleContract.address,
        functionName: 'paused',
        chainId: cratChain.id,
      },
    ],
  });

  const { data: referralInfoResponse, isLoading: isReferralInfoLoading } = useReadContract({
    abi: citCrowdsaleContract.abi,
    address: citCrowdsaleContract.address,
    functionName: 'referralInfo',
    args: [address!],
    chainId: cratChain.id,
    query: {
      enabled: !!address,
    },
  });

  const referralInfo = useMemo(() => {
    if (!referralInfoResponse) {
      return undefined;
    }

    return {
      purchaseMade: referralInfoResponse[0] ?? false,
      referrer: referralInfoResponse[1],
      earned: referralInfoResponse[2],
    };
  }, [referralInfoResponse]);

  return useMemo(
    () => ({
      paymentToken: {
        ...wusdtToken,
        balance: tokenBalance,
      },
      referralInfo,
      citPrice: citPriceResponse?.result,
      minPaymentAmount: minPaymentResponse?.result,
      totalSold: totalSoldResponse?.result,
      isPaused: pausedResponse?.result ?? false,
      loading: {
        isBalanceLoading,
        isPublicSaleInfoLoading,
        isReferralInfoLoading,
      },
    }),
    [
      tokenBalance,
      referralInfo,
      citPriceResponse,
      minPaymentResponse,
      isBalanceLoading,
      isPublicSaleInfoLoading,
      isReferralInfoLoading,
      totalSoldResponse,
      pausedResponse,
    ],
  );
};
